(() => {
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  root.classList.add('lab-js');

  function initialize(scope = document) {
    scope.querySelectorAll('[data-lab-tone-toggle]').forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.hidden = false;
      const sync = () => button.setAttribute('aria-pressed', String(root.dataset.labTone === 'dark'));
      sync();
      button.addEventListener('click', () => {
        const change = () => {
          root.dataset.labTone = root.dataset.labTone === 'dark' ? 'light' : 'dark';
          try { localStorage.setItem('producer-labz-tone', root.dataset.labTone); } catch {}
          sync();
          document.querySelectorAll('plugin-audio').forEach(player => player.draw?.());
        };
        if (document.startViewTransition && !reduced.matches) document.startViewTransition(change);
        else change();
      });
    });

    scope.querySelectorAll('[data-lab-catalog]').forEach(catalog => {
      if (catalog.dataset.ready) return;
      catalog.dataset.ready = 'true';
      const cards = [...catalog.querySelectorAll('[data-lab-card]')];
      const controls = [...catalog.querySelectorAll('[data-lab-filter]')];
      const count = catalog.querySelector('[data-lab-count]');
      const empty = catalog.querySelector('[data-lab-filter-empty]');
      const valid = ['all', 'plugins', 'sounds', 'free'];
      const onCatalogPage = catalog.classList.contains('lab-catalog-page');
      const label = catalog.dataset.countLabel || '';
      catalog.querySelectorAll('[data-lab-filter-controls]').forEach(el => { el.hidden = false; });
      function filter(category, updateUrl = false) {
        if (!valid.includes(category)) category = 'all';
        // FLIP: remember where visible cards were, then glide them from there to their new places.
        const before = catalog.dataset.filtered && !reduced.matches ? new Map(cards.filter(card => !card.hidden).map(card => [card, card.getBoundingClientRect()])) : null;
        let total = 0;
        cards.forEach(card => {
          const show = category === 'all' || (category === 'free' ? card.dataset.free === 'true' : card.dataset.category === category);
          card.hidden = !show;
          if (show) total++;
        });
        if (before) {
          let entering = 0;
          cards.forEach(card => {
            if (card.hidden) return;
            const was = before.get(card);
            if (!was) {
              card.animate([{ opacity: 0, transform: 'translateY(14px) scale(.96)' }, { opacity: 1, transform: 'none' }], { duration: 520, delay: Math.min(entering++, 8) * 45, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' });
              return;
            }
            const now = card.getBoundingClientRect();
            const dx = was.left - now.left, dy = was.top - now.top;
            if (dx || dy) card.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 560, easing: 'cubic-bezier(.2,.8,.2,1)' });
          });
        }
        catalog.dataset.filtered = 'true';
        controls.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.labFilter === category)));
        if (count) count.textContent = `${String(total).padStart(2, '0')}${label ? ` ${label}` : ''}`;
        if (empty) empty.hidden = total > 0 || cards.length === 0;
        if (updateUrl && onCatalogPage) {
          const url = new URL(location.href);
          if (category === 'all') url.searchParams.delete('category');
          else url.searchParams.set('category', category);
          history.replaceState(null, '', url);
        }
      }
      filter(onCatalogPage ? new URL(location.href).searchParams.get('category') || 'all' : 'all');
      // Paint the pressed state first, then filter on the next task, so the tap responds immediately.
      controls.forEach(button => button.addEventListener('click', () => {
        controls.forEach(other => other.setAttribute('aria-pressed', String(other === button)));
        requestAnimationFrame(() => setTimeout(() => filter(button.dataset.labFilter, true)));
      }));
      catalog.querySelector('[data-lab-reset]')?.addEventListener('click', () => filter('all', true));
      catalog.querySelector('[data-lab-sort]')?.addEventListener('change', event => {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        location.assign(url);
      });
      if (onCatalogPage) window.addEventListener('popstate', () => filter(new URL(location.href).searchParams.get('category') || 'all'));
    });

    scope.querySelectorAll('[data-lab-product]').forEach(product => {
      if (product.dataset.ready) return;
      product.dataset.ready = 'true';
      const price = product.querySelector('[data-lab-price]');
      const add = product.querySelector('[data-lab-add]');
      const addLabel = product.querySelector('[data-lab-add-label]');
      product.querySelectorAll('[data-lab-variant]').forEach(input => input.addEventListener('change', () => {
        if (!input.checked) return;
        if (price) price.textContent = input.dataset.price;
        const available = input.dataset.available === 'true';
        if (add) add.disabled = !available;
        if (addLabel) addLabel.textContent = available ? add.dataset.addLabel : add.dataset.soldLabel;
        const url = new URL(location.href);
        url.searchParams.set('variant', input.value);
        history.replaceState(null, '', url);
      }));
    });

    // "Hear Venin" plays the dashboard's processed demo in place; without the player it stays a link to it.
    scope.querySelectorAll('[data-lab-hear]').forEach(link => {
      if (link.dataset.ready) return;
      const dash = document.querySelector('plugin-audio.lab-dash');
      if (!dash) return;
      link.dataset.ready = 'true';
      const label = link.querySelector('[data-lab-hear-label]');
      const sync = () => {
        const playing = dash.classList.contains('is-playing');
        link.toggleAttribute('data-playing', playing);
        label.textContent = playing ? link.dataset.labelPause : link.dataset.labelPlay;
      };
      new MutationObserver(sync).observe(dash, { attributes: true, attributeFilter: ['class'] });
      link.addEventListener('click', event => {
        if (typeof dash.play !== 'function') return;
        event.preventDefault();
        if (dash.playing) dash.pause();
        else { dash.setVersion('wet'); dash.play(); }
      });
      customElements.whenDefined('plugin-audio').then(() => link.setAttribute('role', 'button'));
    });

    scope.querySelectorAll('[data-lab-folders]').forEach(group => {
      if (group.dataset.ready) return;
      group.dataset.ready = 'true';
      // Touch screens keep the folders as links to the filtered catalog.
      const desktop = matchMedia('(hover: hover) and (pointer: fine)');
      const folders = [...group.querySelectorAll('[data-lab-folder]')].map(item => {
        const trigger = item.querySelector('[data-lab-folder-trigger]');
        return { item, trigger, tray: document.getElementById(trigger.getAttribute('aria-controls')) };
      });
      let open = null;
      const spring = 'cubic-bezier(.22,1.2,.36,1)';

      function enhance() {
        group.classList.toggle('is-enhanced', desktop.matches);
        folders.forEach(({ trigger }) => {
          if (desktop.matches) {
            trigger.setAttribute('role', 'button');
            trigger.setAttribute('aria-expanded', String(open?.trigger === trigger));
          } else {
            trigger.removeAttribute('role');
            trigger.removeAttribute('aria-expanded');
          }
        });
        if (!desktop.matches && open) close(open, false);
      }

      // The tray cards start stacked on the folder and fly into their places.
      function flyOut(folder) {
        const from = folder.item.querySelector('.lab-folder').getBoundingClientRect();
        const cards = [...folder.tray.querySelectorAll('[data-lab-tray-card]')];
        const height = folder.tray.scrollHeight;
        folder.tray.animate([{ height: '0px' }, { height: `${height}px` }], { duration: 520, easing: 'cubic-bezier(.2,.8,.2,1)' });
        cards.forEach((card, index) => {
          const to = card.getBoundingClientRect();
          const x = from.left + from.width / 2 - (to.left + to.width / 2);
          const y = from.top + from.height * .35 - (to.top + to.height / 2);
          const turn = (index % 2 ? 1 : -1) * (6 + index * 2);
          card.animate([
            { transform: `translate(${x}px, ${y}px) scale(.42) rotate(${turn}deg)`, opacity: 0 },
            { opacity: 1, offset: .25 },
            { transform: 'none', opacity: 1 }
          ], { duration: 820, delay: index * 55, easing: spring, fill: 'backwards' });
        });
      }

      function openFolder(folder) {
        if (open && open !== folder) close(open, false);
        open = folder;
        folder.item.classList.add('is-open');
        folder.trigger.setAttribute('aria-expanded', 'true');
        warm(folder);
        folder.tray.hidden = false;
        if (!reduced.matches) flyOut(folder);
        const bottom = folder.tray.getBoundingClientRect().bottom;
        if (bottom > innerHeight) scrollBy({ top: Math.min(bottom - innerHeight + 40, folder.tray.getBoundingClientRect().top - 120), behavior: reduced.matches ? 'auto' : 'smooth' });
      }

      function close(folder, animate = true) {
        folder.item.classList.remove('is-open');
        folder.trigger.setAttribute('aria-expanded', 'false');
        if (open === folder) open = null;
        if (!animate || reduced.matches) { folder.tray.hidden = true; return; }
        const height = folder.tray.offsetHeight;
        folder.tray.animate([{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.4,0,.2,1)' })
          .finished.then(() => { if (open !== folder) folder.tray.hidden = true; }, () => {});
      }

      // Tray images are lazy inside a hidden tray; fetch them once a folder is likely to open.
      const warm = folder => folder.tray.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });

      folders.forEach(folder => {
        folder.trigger.addEventListener('pointerenter', () => warm(folder), { once: true });
        folder.trigger.addEventListener('focus', () => warm(folder), { once: true });
        folder.trigger.addEventListener('click', event => {
          if (!desktop.matches) return;
          event.preventDefault();
          if (open === folder) close(folder);
          else openFolder(folder);
        });
        folder.trigger.addEventListener('keydown', event => {
          if (event.key === ' ' && desktop.matches) { event.preventDefault(); folder.trigger.click(); }
        });
      });
      group.addEventListener('keydown', event => {
        if (event.key === 'Escape' && open) { const { trigger } = open; close(open); trigger.focus(); }
      });
      desktop.addEventListener('change', enhance);
      enhance();
    });

    if (!reduced.matches && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }), { threshold: .08, rootMargin: '0px 0px 35px' });
      scope.querySelectorAll('[data-lab-reveal]').forEach((el, index) => {
        if (el.getBoundingClientRect().top < innerHeight) return;
        el.classList.add('lab-will-reveal');
        el.style.transitionDelay = `${Math.min(index % 3, 2) * 55}ms`;
        observer.observe(el);
      });
    }

    if (!reduced.matches && matchMedia('(pointer: fine)').matches) {
      scope.querySelectorAll('[data-lab-tilt]').forEach(el => {
        if (el.dataset.tiltReady) return;
        el.dataset.tiltReady = 'true';
        let frame = 0;
        el.addEventListener('pointermove', event => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            el.style.setProperty('--tilt-x', ((event.clientX - rect.left) / rect.width - .5).toFixed(3));
            el.style.setProperty('--tilt-y', ((event.clientY - rect.top) / rect.height - .5).toFixed(3));
          });
        });
        el.addEventListener('pointerleave', () => {
          cancelAnimationFrame(frame);
          el.style.setProperty('--tilt-x', '0');
          el.style.setProperty('--tilt-y', '0');
        });
      });
    }
  }
  // Slide-in cart: product forms add through the Ajax API and the drawer re-renders
  // through the Section Rendering API. Any failure falls back to the normal /cart pages.
  const drawer = document.querySelector('[data-lab-drawer]');
  if (drawer && typeof drawer.showModal === 'function' && window.fetch) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const post = (path, data) => fetch(rootUrl + path, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(data) });

    async function refresh() {
      const response = await fetch(`${rootUrl}?sections=lab-cart-drawer`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = (await response.json())['lab-cart-drawer'];
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.lab-drawer__panel');
      drawer.querySelector('.lab-drawer__panel').replaceWith(section);
      document.querySelectorAll('[data-lab-cart-count]').forEach(el => { el.textContent = section.dataset.labDrawerCount; });
    }
    function open() {
      if (!drawer.open) drawer.showModal();
      requestAnimationFrame(() => drawer.classList.add('is-open'));
    }
    function close() {
      drawer.classList.remove('is-open');
      setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.close(); }, reduced.matches ? 0 : 380);
    }

    drawer.addEventListener('cancel', event => { event.preventDefault(); close(); });
    drawer.addEventListener('click', async event => {
      if (event.target === drawer || event.target.closest('[data-lab-drawer-close]')) return close();
      const remove = event.target.closest('[data-lab-remove]');
      if (!remove) return;
      remove.disabled = true;
      try {
        const response = await post('cart/change.js', { line: remove.dataset.labRemove, quantity: 0 });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await refresh();
        drawer.querySelector('[data-lab-remove], [data-lab-drawer-close]')?.focus();
      } catch { location.assign(rootUrl + 'cart'); }
    });

    document.querySelectorAll('.lab-cart-link').forEach(link => link.addEventListener('click', async event => {
      event.preventDefault();
      try { await refresh(); open(); } catch { location.assign(link.href); }
    }));

    document.addEventListener('submit', async event => {
      const form = event.target.closest('form.lab-product-form');
      if (!form || (event.submitter && event.submitter.name !== 'add')) return;
      event.preventDefault();
      const button = form.querySelector('[data-lab-add]');
      const status = form.querySelector('[data-lab-status]');
      button?.setAttribute('aria-busy', 'true');
      if (status) status.textContent = '';
      try {
        const response = await post('cart/add.js', new FormData(form));
        if (!response.ok) {
          const problem = await response.json().catch(() => ({}));
          if (status) status.textContent = problem.description || problem.message || status.dataset.error;
          return;
        }
        await refresh();
        open();
      } catch {
        form.submit();
      } finally {
        button?.removeAttribute('aria-busy');
      }
    });
  }

  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.lab-mobile-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary')?.focus(); });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.lab-mobile-menu[open]').forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
  });
})();
